import { useQuery } from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import { graphql } from '@/gql';
import { GetProjectSolarPotentialQuery } from '@/gql/graphql';
import projectKeys from './keyFactory';

/**
 * A sibling of `RecommendedApplianceFields` (heat pump) rather than an
 * extension of it, so the heat pump recommendation queries stay untouched.
 * `panelsCount` is only populated for SOLAR_PANEL recommendations.
 */
graphql(`
  fragment SolarRecommendedApplianceFields on RecommendedAppliance {
    panelsCount
    quantity
    totalAcKw
    warnings
    appliance {
      id
      items {
        id
      }
      totalPrice {
        value
        currency {
          code
          exponent
        }
      }
      productSpec {
        id
        name
        category
        image {
          url
        }
        nominalOutputKw
        soundPowerDbA
        type
        domesticHotWaterLitres
        manufacturer
        panelCapacityW
        solarPanelWidthM
        solarPanelHeightM
        acOutputKw
        mpptCount
        maxInputVoltageV
        maxModuleIscA
        inputsPerMppt
        maxMpptIscA
        minMpptVoltageV
        openCircuitVoltageV
        shortCircuitCurrentA
        vocTemperatureCoefficientPctPerC
        inverterTopology
        hybrid
        phases
        usableCapacityKwh
        maxChargeDischargeKw
        integratedInverter
        pvConversion {
          acOutputKw
          maxPvInputKw
          mpptCount
          maxChargeKw
          maxDischargeKw
          sourceUrl
          verifiedAt
          variant
          ratingConditions
        }
      }
    }
  }
`);

/**
 * The full solar potential summary: everything the Property tab card and the
 * solar designer need. Shared (by name) with the FetchProjectSolarPotential
 * mutation so both paths hydrate the same shape.
 */
graphql(`
  fragment SolarEstimateSummaryFields on SolarEstimateSummary {
    roofRecoveryReason
    sourceAnalysisId
    sourceAnalysisVersion
    available
    imageryQuality
    imageryDate
    centre {
      lat
      lng
    }
    panelWidthMeters
    panelHeightMeters
    panelCapacityWatts
    maxPanelsCount
    maxSunshineHoursPerYear
    roofSegments {
      pitchDegrees
      azimuthDegrees
      areaMeters2
    }
    panels {
      centre {
        lat
        lng
      }
      orientation
      segmentIndex
      annualAcKwh
    }
    configurations {
      panelsCount
      annualAcKwh
      estimatedAnnualSavings
      estimatedPaybackYears
    }
    defaultConfigurationIndex
    carbonOffsetFactorKgPerMwh
    panelLifetimeYears
    netMeteringAllowed
    recommendedPanelAppliances {
      ...SolarRecommendedApplianceFields
    }
    recommendedInverterAppliances {
      ...SolarRecommendedApplianceFields
    }
    recommendedBatteryAppliances {
      ...SolarRecommendedApplianceFields
    }
    geometry {
      faces {
        polygon {
          outer {
            lat
            lng
          }
          holes {
            lat
            lng
          }
        }
        segmentIndex
        photoAgreement {
          agrees
          note
        }
        roofPitchDegrees
        planeFit {
          status
          sampleCount
          eligiblePixelCount
          outsidePixelCount
          rmsMetres
          coverage
          reasons
          confidence
          note
        }
        roofAzimuthDegrees
        panelsOnTiltFrames
        generation
        pitchDegrees
        azimuthDegrees
        areaMeters2
        shading {
          annualSunAccessPercent
          monthlySunAccessPercent
          hourlySunAccessPercent
        }
      }
      obstructions {
        lat
        lng
      }
      packedPanels {
        corners {
          lat
          lng
        }
        faceIndex
        segmentIndex
        orientation
        annualAcKwh
        monthlyShares
        mcsYearlyEnergyKwh
        onProperty
      }
      edited
      panelDimensions {
        widthMeters
        heightMeters
        capacityWatts
      }
      stats {
        faceCount
        faceAreaMeters2
        panelsCount
      }
      roofModelProvider
      parcel {
        source
        inspireId
        ring {
          lat
          lng
        }
        areaMeters2
        attribution
      }
      spansMultipleProperties
    }
    fluxOverlay {
      url
      bounds {
        north
        south
        east
        west
      }
    }
    rgbOverlay {
      url
      bounds {
        north
        south
        east
        west
      }
    }
  }
`);

const GetProjectSolarPotential = graphql(`
  query GetProjectSolarPotential($projectId: ID!) {
    projectById(id: $projectId) {
      id
      solarPotential {
        ...SolarEstimateSummaryFields
      }
    }
  }
`);

export type ProjectSolarPotential = NonNullable<
  GetProjectSolarPotentialQuery['projectById']['solarPotential']
>;

const useGetProjectSolarPotential = (projectId?: string) => {
  const { data, ...rest } = useQuery({
    enabled: !!projectId,
    queryKey: projectKeys.solarPotential(projectId!),
    queryFn: () => {
      return gqlClient.request(GetProjectSolarPotential, {
        projectId: projectId!,
      });
    },
  });
  return {
    solarPotential: data?.projectById?.solarPotential,
    data,
    ...rest,
  };
};

export default useGetProjectSolarPotential;
